import React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExternalLinkAltIcon } from '@patternfly/react-icons';
import { useReconciliationOverview } from '../../../hooks/useAdminReconciliation';
import type {
  MollieCompensation,
  MollieInvoice,
  MollieMonthPayout,
  MollieReportCategory,
  ReconciliationOverview,
  SettledThroughEntry,
  WalletMonthMovement,
} from '../../../types/models/Reconciliation';
import { formatBareDateNumeric, formatInstantBrussels, formatMonthYear } from '../../../utils/dateFormat';
import {
  AMOUNT_CELL,
  CheckLabel,
  EmptyListState,
  FetchedAt,
  LiveReadSpinner,
  Money,
  MonthPicker,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  SearchableTable,
  SettlementRef,
  StaleDataAlert,
  matchesQuery,
} from './ReconciliationParts';
import {
  COST_CLASS_LABELS,
  COST_CLASS_ORDER,
  MOLLIE_INVOICE_STATUS_LABELS,
  PURPOSE_LABELS,
  REPORT_CATEGORY_LABELS,
  TRANSACTION_TYPE_LABELS,
  TRANSFER_FREQUENCY_LABELS,
  WALLET_KIND_LABELS,
  WALLET_REFERENCE_TYPE_LABELS,
  flagLabel,
  flagMeaning,
  labelOf,
} from './reconciliationLabels';

interface OverviewTabProps {
  month: string;
  onMonthChange: (month: string) => void;
  isActive: boolean;
  /** Open the Mollie journal on this month with « Signalées seulement ». */
  onOpenFlaggedJournal: () => void;
}

/** One DescriptionList row. */
const Row: React.FunctionComponent<{ term: string; children: React.ReactNode }> = ({ term, children }) => (
  <DescriptionListGroup>
    <DescriptionListTerm>{term}</DescriptionListTerm>
    <DescriptionListDescription>{children}</DescriptionListDescription>
  </DescriptionListGroup>
);

const PDF_LINK_HINT = 'Lien signé Mollie, valable jusqu’à l’expiration indiquée — ouvrez-le, ne le conservez pas.';

/** A Mollie invoice reference with its signed PDF link (opened, never stored). */
export const MollieInvoicePdfLink: React.FunctionComponent<{ invoice: MollieInvoice }> = ({ invoice }) =>
  invoice.pdf_url ? (
    <Button
      variant="link"
      isInline
      component="a"
      href={invoice.pdf_url}
      target="_blank"
      rel="noopener noreferrer"
      icon={<ExternalLinkAltIcon />}
      iconPosition="end"
      title={
        invoice.pdf_expires_at
          ? `${PDF_LINK_HINT} Expire le ${formatInstantBrussels(invoice.pdf_expires_at)}.`
          : PDF_LINK_HINT
      }
    >
      PDF
    </Button>
  ) : null;

const MollieBalanceCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const { opening, closing, balance } = data.mollie;
  const snapshot = (s: typeof opening) =>
    s === null ? (
      '—'
    ) : (
      <>
        <Money value={s.total} />
        <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
          en attente <Money value={s.pending} /> · disponible <Money value={s.available} />
        </div>
      </>
    );
  return (
    <Card isFullHeight>
      <CardTitle>Solde Mollie</CardTitle>
      <CardBody>
        <DescriptionList isCompact>
          <Row term="Ouverture">{snapshot(opening)}</Row>
          <Row term="Clôture">{snapshot(closing)}</Row>
          <Row term="Versements">{labelOf(TRANSFER_FREQUENCY_LABELS, balance.transfer_frequency)}</Row>
        </DescriptionList>
      </CardBody>
    </Card>
  );
};

const JournalCheckCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const { check, totals } = data.mollie;
  return (
    <Card isFullHeight>
      <CardTitle>Contrôle du journal</CardTitle>
      <CardBody>
        {check === null ? (
          <EmptyListState title="Pas de contrôle pour cette période" />
        ) : (
          <DescriptionList isCompact>
            <Row term="Variation du solde (rapport Mollie)">
              <Money value={check.report_change} />
            </Row>
            <Row term="Somme nette du journal">
              <Money value={check.transactions_net} />
            </Row>
            <Row term="Résultat">
              <CheckLabel ok={check.ok} difference={check.difference} />
            </Row>
            <Row term="Mouvements">{totals.count}</Row>
            <Row term="Payés, pas encore sur le solde">
              {check.pending_payments.count === 0 ? (
                'Aucun'
              ) : (
                <Label status="warning" isCompact>
                  {check.pending_payments.count} · <Money value={check.pending_payments.gross} />
                </Label>
              )}
            </Row>
          </DescriptionList>
        )}
      </CardBody>
    </Card>
  );
};

const FeesCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const fees = data.mollie.fees;
  return (
    <Card isFullHeight>
      <CardTitle>Frais Mollie de {formatMonthYear(fees.period)}</CardTitle>
      <CardBody>
        <DescriptionList isCompact>
          <Row term="Retenus par les versements">
            <Money value={fees.withheld} />
          </Row>
          <Row term="Compensations">
            <Money value={fees.compensations} />
          </Row>
          <Row term="Retenu net">
            <Money value={fees.settled_total} />
          </Row>
          <Row term="Facturé par Mollie">
            {fees.invoice_issued ? <Money value={fees.invoiced} /> : 'Facture pas encore émise'}
          </Row>
          <Row term="Résultat">
            <CheckLabel ok={fees.ok} difference={fees.difference} />
          </Row>
          {fees.invoices.length > 0 && (
            <Row term={fees.invoices.length === 1 ? 'Facture Mollie' : 'Factures Mollie'}>
              {fees.invoices.map((inv, i) => (
                <div key={inv.id ?? i}>
                  {inv.reference ?? inv.id ?? '—'} ({labelOf(MOLLIE_INVOICE_STATUS_LABELS, inv.status)}){' '}
                  <MollieInvoicePdfLink invoice={inv} />
                </div>
              ))}
            </Row>
          )}
        </DescriptionList>
      </CardBody>
    </Card>
  );
};

/** The fees by cost class, then per payout period (a breakdown of `settled_total`, already allocated). */
const FeesBreakdownCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const fees = data.mollie.fees;
  return (
    <Card>
      <CardTitle>Ventilation des frais du mois</CardTitle>
      <CardBody>
        <Table aria-label="Frais du mois par catégorie" variant="compact" className="pf-v6-u-mb-lg">
          <Thead>
            <Tr>
              <Th>Catégorie</Th>
              <Th className={AMOUNT_CELL}>Montant</Th>
            </Tr>
          </Thead>
          <Tbody>
            {COST_CLASS_ORDER.map((c) => (
              <Tr key={c}>
                <Td dataLabel="Catégorie">{COST_CLASS_LABELS[c]}</Td>
                <Td dataLabel="Montant" className={AMOUNT_CELL}>
                  <Money value={fees.by_class[c]} />
                </Td>
              </Tr>
            ))}
            <Tr>
              <Td dataLabel="Catégorie">
                <strong>Retenu net</strong>
              </Td>
              <Td dataLabel="Montant" className={AMOUNT_CELL}>
                <strong>
                  <Money value={fees.settled_total} />
                </strong>
              </Td>
            </Tr>
          </Tbody>
        </Table>
        {fees.settled_through.length === 0 ? (
          <EmptyListState title="Aucun versement n'a retenu de frais pour ce mois" />
        ) : (
          <Table aria-label="Frais du mois par versement" variant="compact">
            <Thead>
              <Tr>
                <Th>Versement</Th>
                <Th>Versé le</Th>
                <Th className={AMOUNT_CELL}>Frais</Th>
                <Th className={AMOUNT_CELL}>Arrondi</Th>
                <Th className={AMOUNT_CELL}>Compensation</Th>
                <Th className={AMOUNT_CELL}>Remise</Th>
                <Th className={AMOUNT_CELL}>Autre</Th>
                <Th className={AMOUNT_CELL}>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fees.settled_through.map((e: SettledThroughEntry) => (
                <Tr key={`${e.settlement_id}-${e.period}`}>
                  <Td dataLabel="Versement">
                    <SettlementRef id={e.settlement_id} reference={e.settlement_reference} />
                  </Td>
                  <Td dataLabel="Versé le">{formatInstantBrussels(e.settled_at)}</Td>
                  <Td dataLabel="Frais" className={AMOUNT_CELL}>
                    <Money value={e.fees} />
                  </Td>
                  <Td dataLabel="Arrondi" className={AMOUNT_CELL}>
                    <Money value={e.rounding} />
                  </Td>
                  <Td dataLabel="Compensation" className={AMOUNT_CELL}>
                    <Money value={e.compensation} />
                  </Td>
                  <Td dataLabel="Remise" className={AMOUNT_CELL}>
                    <Money value={e.discount} />
                  </Td>
                  <Td dataLabel="Autre" className={AMOUNT_CELL}>
                    <Money value={e.other} />
                  </Td>
                  <Td dataLabel="Total" className={AMOUNT_CELL}>
                    <Money value={e.total} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

const matchPayout = (p: MollieMonthPayout, q: string) => matchesQuery(q, [p.reference, p.id]);
const matchCompensation = (c: MollieCompensation, q: string) =>
  matchesQuery(q, [c.mollie_invoice_reference, c.mollie_invoice_id, c.transaction_id]);
const matchCategory = (c: MollieReportCategory, q: string) =>
  matchesQuery(q, [c.category, labelOf(REPORT_CATEGORY_LABELS, c.category)]);
const matchMovement = (m: WalletMonthMovement, q: string) =>
  matchesQuery(q, [
    m.kind,
    labelOf(WALLET_KIND_LABELS, m.kind),
    m.reference_type,
    m.reference_type === null ? null : labelOf(WALLET_REFERENCE_TYPE_LABELS, m.reference_type),
  ]);

const JournalTotalsCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const { totals } = data.mollie;
  const head = (first: string) => (
    <Thead>
      <Tr>
        <Th>{first}</Th>
        <Th className={AMOUNT_CELL}>Nombre</Th>
        <Th className={AMOUNT_CELL}>Brut</Th>
        <Th className={AMOUNT_CELL}>Frais</Th>
        <Th className={AMOUNT_CELL}>Net</Th>
      </Tr>
    </Thead>
  );
  const amounts = (r: { count: number; gross: string; fees: string; net: string }) => (
    <>
      <Td dataLabel="Nombre" className={AMOUNT_CELL}>
        {r.count}
      </Td>
      <Td dataLabel="Brut" className={AMOUNT_CELL}>
        <Money value={r.gross} />
      </Td>
      <Td dataLabel="Frais" className={AMOUNT_CELL}>
        <Money value={r.fees} />
      </Td>
      <Td dataLabel="Net" className={AMOUNT_CELL}>
        <Money value={r.net} />
      </Td>
    </>
  );
  const grandTotal = (first: string) => (
    <Tr>
      <Td dataLabel={first}>
        <strong>Total du mois</strong>
      </Td>
      {amounts(totals)}
    </Tr>
  );
  return (
    <Card>
      <CardTitle>Totaux du journal Mollie</CardTitle>
      <CardBody>
        <Table aria-label="Totaux du journal par type de mouvement" variant="compact" className="pf-v6-u-mb-lg">
          {head('Type de mouvement')}
          <Tbody>
            {totals.by_type.map((r) => (
              <Tr key={r.type}>
                <Td dataLabel="Type de mouvement">{labelOf(TRANSACTION_TYPE_LABELS, r.type)}</Td>
                {amounts(r)}
              </Tr>
            ))}
            {grandTotal('Type de mouvement')}
          </Tbody>
        </Table>
        <Table aria-label="Totaux du journal par objet" variant="compact">
          {head('Objet')}
          <Tbody>
            {totals.by_purpose.map((r) => (
              <Tr key={r.purpose ?? 'none'}>
                <Td dataLabel="Objet">{r.purpose === null ? 'Sans objet' : labelOf(PURPOSE_LABELS, r.purpose)}</Td>
                {amounts(r)}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

const FlagsCard: React.FunctionComponent<{ data: ReconciliationOverview; onOpen: () => void }> = ({ data, onOpen }) => (
  <Card isFullHeight>
    <CardHeader
      actions={
        data.flags.length > 0
          ? {
              actions: (
                <Button variant="link" isInline onClick={onOpen}>
                  Voir dans le journal
                </Button>
              ),
            }
          : undefined
      }
    >
      <CardTitle>Signalements</CardTitle>
    </CardHeader>
    <CardBody>
      {data.flags.length === 0 ? (
        <EmptyListState title="Aucun signalement ce mois-ci" />
      ) : (
        <DescriptionList isCompact>
          {data.flags.map((f) => (
            <Row key={f.flag} term={flagLabel(f.flag)}>
              <strong>{f.count}</strong>
              {flagMeaning(f.flag) && (
                <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">{flagMeaning(f.flag)}</div>
              )}
            </Row>
          ))}
        </DescriptionList>
      )}
    </CardBody>
  </Card>
);

const WalletCard: React.FunctionComponent<{ data: ReconciliationOverview }> = ({ data }) => {
  const w = data.wallet;
  return (
    <Card>
      <CardTitle>Solde prépayé des clients</CardTitle>
      <CardBody>
        <Grid hasGutter className="pf-v6-u-mb-md">
          <GridItem md={6}>
            <DescriptionList isCompact>
              <Row term="Dette envers les clients — ouverture">
                <Money value={w.opening_liability} />
              </Row>
              <Row term="Dette envers les clients — clôture">
                <Money value={w.closing_liability} />
              </Row>
            </DescriptionList>
          </GridItem>
          <GridItem md={6}>
            <DescriptionList isCompact>
              <Row term="Créances sur les clients — ouverture">
                <Money value={w.opening_receivable} />
              </Row>
              <Row term="Créances sur les clients — clôture">
                <Money value={w.closing_receivable} />
              </Row>
              <Row term="Contrôle du grand livre">
                <CheckLabel ok={w.check.ok} />
                {!w.check.ok && (
                  <div className="pf-v6-u-font-size-sm">
                    Somme du grand livre <Money value={w.check.ledger_total} /> · somme des soldes{' '}
                    <Money value={w.check.balances_total} />
                    {w.check.undated_rows > 0 && ` · ${w.check.undated_rows} ligne(s) sans date`}
                  </div>
                )}
              </Row>
            </DescriptionList>
          </GridItem>
        </Grid>
        <SearchableTable<WalletMonthMovement>
          rows={w.movements}
          rowKey={(m) => `${m.kind}-${m.reference_type ?? 'none'}`}
          matches={matchMovement}
          placeholder="Rechercher par type ou référence..."
          ariaLabel="Mouvements du solde prépayé du mois"
          emptyTitle="Aucun mouvement du solde prépayé ce mois-ci"
          columns={[
            { title: 'Type', cell: (m) => labelOf(WALLET_KIND_LABELS, m.kind) },
            {
              title: 'Référence',
              cell: (m) => (m.reference_type === null ? '—' : labelOf(WALLET_REFERENCE_TYPE_LABELS, m.reference_type)),
            },
            { title: 'Nombre', cell: (m) => m.count, numeric: true },
            { title: 'Montant', cell: (m) => <Money value={m.amount} />, numeric: true },
          ]}
          footer={
            Object.keys(w.totals_by_kind).length > 0 && (
              <DescriptionList isCompact isHorizontal className="pf-v6-u-mt-md">
                {Object.entries(w.totals_by_kind).map(([kind, amount]) => (
                  <Row key={kind} term={`Total ${labelOf(WALLET_KIND_LABELS, kind).toLowerCase()}`}>
                    <Money value={amount} />
                  </Row>
                ))}
              </DescriptionList>
            )
          }
        />
      </CardBody>
    </Card>
  );
};

/**
 * Vue du mois — `GET /admin/reconciliation/overview`: the Mollie balance and its self-check, the fees
 * withheld vs Mollie's invoice, the payouts and compensations, the flags and the prepaid wallets.
 */
const ReconciliationOverviewTab: React.FunctionComponent<OverviewTabProps> = ({
  month,
  onMonthChange,
  isActive,
  onOpenFlaggedJournal,
}) => {
  const { data, loading, error, fetchedAt, refetch } = useReconciliationOverview(month, isActive);

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <MonthPicker month={month} onChange={onMonthChange} />
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
    </Flex>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState title="Impossible de charger la vue du mois" message={error} onRetry={() => void refetch()} />
        ) : (
          <LiveReadSpinner what="vue du mois" />
        )}
      </>
    );
  }

  const { mollie } = data;
  return (
    <>
      {header}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners
        warnings={data.warnings}
        context={{ period: data.period, check: mollie.check, fees: mollie.fees }}
        onRetry={() => void refetch()}
      />
      <Grid hasGutter>
        <GridItem md={6} xl={4}>
          <MollieBalanceCard data={data} />
        </GridItem>
        <GridItem md={6} xl={4}>
          <JournalCheckCard data={data} />
        </GridItem>
        <GridItem md={12} xl={4}>
          <FeesCard data={data} />
        </GridItem>
        <GridItem xl={6}>
          <Card isFullHeight>
            <CardTitle>Versements du mois</CardTitle>
            <CardBody>
              <SearchableTable<MollieMonthPayout>
                rows={mollie.payouts}
                rowKey={(p) => p.id}
                matches={matchPayout}
                placeholder="Rechercher par référence bancaire..."
                ariaLabel="Versements du mois"
                emptyTitle="Aucun versement ce mois-ci"
                columns={[
                  { title: 'Référence bancaire', cell: (p) => <SettlementRef id={p.id} reference={p.reference} /> },
                  { title: 'Versé le', cell: (p) => formatInstantBrussels(p.settled_at) },
                  { title: 'Revenus', cell: (p) => <Money value={p.revenue} />, numeric: true },
                  { title: 'Frais', cell: (p) => <Money value={p.costs} />, numeric: true },
                  { title: 'Montant versé', cell: (p) => <Money value={p.amount} />, numeric: true },
                ]}
                footer={
                  <p className="pf-v6-u-mt-md pf-v6-u-text-align-end">
                    Total versé du mois :{' '}
                    <strong>
                      <Money value={mollie.payouts_total} />
                    </strong>
                  </p>
                }
              />
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xl={6}>
          <Card isFullHeight>
            <CardTitle>Compensations reçues</CardTitle>
            <CardBody>
              <p className="pf-v6-u-mb-md pf-v6-u-text-color-subtle">
                L&apos;arrondi que Mollie rend pour la facture d&apos;un mois précédent.
              </p>
              <SearchableTable<MollieCompensation>
                rows={mollie.compensations}
                rowKey={(c, i) => c.transaction_id ?? `c-${i}`}
                matches={matchCompensation}
                placeholder="Rechercher par facture Mollie..."
                ariaLabel="Compensations de facture reçues"
                emptyTitle="Aucune compensation ce mois-ci"
                columns={[
                  { title: 'Date', cell: (c) => formatBareDateNumeric(c.booking_date) },
                  { title: 'Facture Mollie', cell: (c) => c.mollie_invoice_reference ?? c.mollie_invoice_id ?? '—' },
                  { title: 'Montant', cell: (c) => <Money value={c.amount} />, numeric: true },
                ]}
              />
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xl={6}>
          <FeesBreakdownCard data={data} />
        </GridItem>
        <GridItem xl={6}>
          <Card isFullHeight>
            <CardTitle>Rapport Mollie par catégorie</CardTitle>
            <CardBody>
              <SearchableTable<MollieReportCategory>
                rows={mollie.categories}
                rowKey={(c) => c.category}
                matches={matchCategory}
                placeholder="Rechercher par catégorie..."
                ariaLabel="Rapport de solde Mollie par catégorie"
                emptyTitle="Aucun mouvement dans le rapport Mollie"
                columns={[
                  { title: 'Catégorie', cell: (c) => labelOf(REPORT_CATEGORY_LABELS, c.category) },
                  { title: 'En attente', cell: (c) => <Money value={c.pending} />, numeric: true },
                  { title: 'Passé en disponible', cell: (c) => <Money value={c.moved_to_available} />, numeric: true },
                  {
                    title: 'Immédiatement disponible',
                    cell: (c) => <Money value={c.immediately_available} />,
                    numeric: true,
                  },
                  { title: 'Effet net', cell: (c) => <Money value={c.net} />, numeric: true },
                ]}
              />
            </CardBody>
          </Card>
        </GridItem>
        <GridItem xl={6}>
          <JournalTotalsCard data={data} />
        </GridItem>
        <GridItem xl={6}>
          <FlagsCard data={data} onOpen={onOpenFlaggedJournal} />
        </GridItem>
        <GridItem span={12}>
          <WalletCard data={data} />
        </GridItem>
      </Grid>
    </>
  );
};

export default ReconciliationOverviewTab;
